import colorPallete, {
    bodyFonts,
    elevation,
    headlineFonts,
    specialHeadings,
    themeFonts,
} from "@DSG/MobiusOverrides/styles/variables";
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import styled, { css } from "styled-components";

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    titleFieldValue = "titleFieldValue",
    descriptionFieldValue = "descriptionFieldValue",
    quoteDescription = "quoteDescription",
    image = "image",
    imageAltText = "imageAltText",
    author = "author",
}

interface Props extends WidgetProps {
    fields: {
        [fields.titleFieldValue]: string;
        [fields.quoteDescription]: string;
        [fields.image]: string;
        [fields.imageAltText]: string;
        [fields.author]: string;
    };
}

export interface DsgQuoteStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    description?: TypographyPresentationProps;
}

export const dsgQuoteStyles: DsgQuoteStyles = {
    wrapper: {
        css: css`
            max-width: 1023px;
            margin: 0 auto;
            padding: 0 56px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 0 40px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
            img {
                margin-bottom: 24px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 16px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
            }
        `,
    },
    description: {
        forwardAs: "div",
        fontFamily: themeFonts.fontFamilyCrimsonPro,
        css: css`
            p[class*="TypographyStyle"] {
                font-size: ${specialHeadings.specialTestimonialLg.fontSize};
                font-family: ${specialHeadings.specialTestimonialLg.fontFamily};
                line-height: ${specialHeadings.specialTestimonialLg.lineHeight};
                font-weight: ${bodyFonts.body1RegularLg.fontWeight};
                color: ${colorPallete.grey700};
                text-align: center;
                margin-bottom: 24px;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: ${specialHeadings.specialTestimonialSm.fontSize};
                                line-height: ${specialHeadings.specialTestimonialSm.lineHeight};
                                margin-bottom: 16px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
};

const QuoteWrapper = styled.div`
    max-width: 1280px;
    margin: 0 auto;
    padding: 48px 0;
    text-align: center;
    height: 100%;
    @media only screen and (max-width: 768px) {
        padding: 40px 0;
    }
`;

const Title = styled.h2`
    font-family: ${headlineFonts.headline2SemiBoldLg.fontFamily};
    font-size: ${headlineFonts.headline2SemiBoldLg.fontSize};
    line-height: ${headlineFonts.headline2SemiBoldLg.lineHeight};
    font-weight: ${headlineFonts.headline2SemiBoldLg.fontWeight};
    margin-bottom: 24px;

    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                css`
                    font-size: ${headlineFonts.headline2SemiBoldSm.fontSize};
                    line-height: ${headlineFonts.headline2SemiBoldSm.lineHeight};
                    margin-bottom: 16px;
                `,
                null,
                null,
            ],
            "max",
        )}
`;

const AuthorName = styled.p`
    position: relative;
    font-size: ${bodyFonts.body1RegularLg.fontSize};
    line-height: ${bodyFonts.body1RegularLg.lineHeight};
    color: ${colorPallete.grey800};
    padding-top: 16px;

    &::after {
        content: "";
        position: absolute;
        width: 100px;
        height: 4px;
        background: ${colorPallete.primaryRed500};
        left: 50%;
        top: 0;
        transform: translate(-50%, 0);
    }
`;

const DsgQuote: React.FC<Props> = props => {
    const styles = dsgQuoteStyles;

    return (
        <QuoteWrapper>
            <StyledWrapper {...styles.wrapper}>
                <Img src={props.fields.image} altText={props.fields.imageAltText || ''}/>
                <Title>{props.fields.titleFieldValue}</Title>
                <Typography {...styles.description}>{parse(props.fields.quoteDescription, parserOptions)}</Typography>
                {props.fields.author !== "" && <AuthorName>{props.fields.author}</AuthorName>}
            </StyledWrapper>
        </QuoteWrapper>
    );
};

const basicTab = {
    displayName: "Common",
    sortOrder: 0,
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.titleFieldValue,
            displayName: "Quote Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "Enter Title",
            isRequired: true,
            tab: basicTab,
        },
        {
            name: fields.quoteDescription,
            fieldType: "Translatable",
            editorTemplate: "RichTextField",
            displayName: "Quote Description 2",
            defaultValue: "",
            extendedConfig: { height: 170 },
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
            tab: basicTab,
        },
        {
            fieldType: "Contextual",
            displayName: "Employee Image",
            name: fields.image,
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            tab: basicTab,
        },
        {
            name: fields.imageAltText,
            editorTemplate: "TextField",
            defaultValue: "Testimonial Author Image",
            fieldType: "General",
        },
        {
            name: fields.author,
            displayName: "Quote Author",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "Enter Title",
            isRequired: true,
            tab: basicTab,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: DsgQuote,
    definition,
};

export default widgetModule;
